"use client";

import React, { useEffect, useRef } from "react";
import { IChartApi, Time, LineSeries, ISeriesApi } from "lightweight-charts";
import { INDICATOR_COLORS } from "../../lib/constants/chart.constants";
import { LineDto } from "../../lib/interface/LineDto.interface";

interface ChartIndicatorsProps {
  chart: IChartApi;
  plasmaLines: LineDto[];
  supLines: LineDto[];
  resLines: LineDto[];
  showPlasma: boolean;
  showSupportResistance?: boolean;
  formatTime: (timestamp: number) => number;
}

export const ChartIndicators: React.FC<ChartIndicatorsProps> = ({
  chart,
  plasmaLines = [],
  supLines = [],
  resLines = [],
  showPlasma,
  showSupportResistance = false,
  formatTime,
}) => {
  const plasmaSeriesRef = useRef<ISeriesApi<"Line">[]>([]);
  const supportSeriesRef = useRef<ISeriesApi<"Line">[]>([]);
  const resistanceSeriesRef = useRef<ISeriesApi<"Line">[]>([]);

  useEffect(() => {
    if (!chart) return;

    // Clear existing plasma series
    plasmaSeriesRef.current.forEach((series) => {
      try {
        chart.removeSeries(series);
      } catch {
        // no-op
      }
    });
    plasmaSeriesRef.current = [];

    // Only create new series if plasma should be shown and we have data
    if (showPlasma && plasmaLines.length > 0) {
      const newSeries = plasmaLines.map((line) => {
        const series = chart.addSeries(LineSeries, {
          color: INDICATOR_COLORS.plasma,
          lineWidth: 1,
          lineStyle: 1,
        });

        series.setData([
          {
            time: formatTime(line.timeStart) as Time,
            value: Number(line.priceStart),
          },
          {
            time: formatTime(line.timeEnd) as Time,
            value: Number(line.priceEnd),
          },
        ]);

        return series;
      });

      plasmaSeriesRef.current = newSeries;
    }

    // Cleanup function to remove series when component unmounts or dependencies change
    return () => {
      plasmaSeriesRef.current.forEach((series) => {
        try {
          chart.removeSeries(series);
        } catch {}
      });
      plasmaSeriesRef.current = [];
    };
  }, [chart, plasmaLines, showPlasma, formatTime]);

  // Support/Resistance rendering
  useEffect(() => {
    if (!chart) return;

    // Clear previous series
    supportSeriesRef.current.forEach((s) => {
      try {
        chart.removeSeries(s);
      } catch {}
    });
    resistanceSeriesRef.current.forEach((s) => {
      try {
        chart.removeSeries(s);
      } catch {}
    });
    supportSeriesRef.current = [];
    resistanceSeriesRef.current = [];

    if (showSupportResistance) {
      if (supLines.length > 0) {
        supportSeriesRef.current = supLines.map((line) => {
          const series = chart.addSeries(LineSeries, {
            color: INDICATOR_COLORS.support,
            lineWidth: 2,
            lineStyle: 0,
          });
          series.setData([
            {
              time: formatTime(line.timeStart) as Time,
              value: Number(line.priceStart),
            },
            {
              time: formatTime(line.timeEnd) as Time,
              value: Number(line.priceEnd),
            },
          ]);
          return series;
        });
      }

      if (resLines.length > 0) {
        resistanceSeriesRef.current = resLines.map((line) => {
          const series = chart.addSeries(LineSeries, {
            color: INDICATOR_COLORS.resistance,
            lineWidth: 1,
            lineStyle: 0,
          });
          series.setData([
            {
              time: formatTime(line.timeStart) as Time,
              value: Number(line.priceStart),
            },
            {
              time: formatTime(line.timeEnd) as Time,
              value: Number(line.priceEnd),
            },
          ]);
          return series;
        });
      }
    }

    return () => {
      supportSeriesRef.current.forEach((s) => {
        try {
          chart.removeSeries(s);
        } catch {}
      });
      resistanceSeriesRef.current.forEach((s) => {
        try {
          chart.removeSeries(s);
        } catch {}
      });
      supportSeriesRef.current = [];
      resistanceSeriesRef.current = [];
    };
  }, [chart, supLines, resLines, showSupportResistance, formatTime]);

  return null; // This is a logic-only component
};
